import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink.js';
import { useLocalizedNavigate, buildLocalizedPath } from '../utils/localizedPath.js';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from '../config/languages.js';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { authStatus, signOut } = useAuthenticator((context) => [
    context.authStatus,
    context.signOut,
  ]);
  const navigate = useNavigate();
  const localizedNavigate = useLocalizedNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    localizedNavigate('/');
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = (event) => {
    if (!event.target.closest('#language-dropdown-container')) {
      setIsDropdownOpen(false);
    }
  };

  const changeLanguage = (lng) => {
    // Navigating updates the URL; <LanguageSync /> then activates the
    // language for real, so the URL stays the single source of truth.
    navigate(buildLocalizedPath(location.pathname + location.search, lng));
    setIsDropdownOpen(false);
  };

  const getLanguageLabel = (lng) => getLanguageByCode(lng)?.label || 'English';

  useEffect(() => {
    window.addEventListener('click', closeDropdown);
    return () => {
      window.removeEventListener('click', closeDropdown);
    };
  }, []);

  return (
    <div className="text-gray-800">
      {/* Navbar */}
      <nav className="sticky top-0 z-10 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-3 flex justify-between items-center gap-2">
          <div className="text-lg sm:text-2xl font-black text-red-600 tracking-tight whitespace-nowrap">
            <LocalizedLink to="/">Kris <span className="text-green-700">Kindler</span></LocalizedLink>
          </div>
          <div className="flex items-center gap-1 sm:gap-4">
            {/* Language Dropdown */}
            <div className="relative" id="language-dropdown-container">
              <button
                id="language-dropdown-button"
                type="button"
                className="inline-flex items-center justify-center px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={toggleDropdown}
                aria-label={getLanguageLabel(i18n.language)}
              >
                {/* Short language code on phones so the navbar fits */}
                <span className="hidden sm:inline">{getLanguageLabel(i18n.language)}</span>
                <span className="sm:hidden uppercase">{i18n.language?.slice(0, 2) || 'en'}</span>
                <svg className="w-5 h-5 ml-1 sm:ml-2 -mr-1 max-[359px]:hidden" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div
                id="language-dropdown-menu"
                className={`origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-20 ${isDropdownOpen ? '' : 'hidden'}`}
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="language-dropdown-button"
              >
                <div className="py-1" role="none">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      role="menuitem"
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {authStatus === 'unauthenticated' ? (
              <>
                {/* Log in/Sign Up button */}
                <LocalizedLink to="/login" aria-label={t('nav.login')} className="p-2 sm:px-4 text-sm font-medium rounded-full text-red-600 hover:text-red-800 transition whitespace-nowrap">
                  {/* Icon-only on phones so the navbar fits in every language */}
                  <svg className="h-5 w-5 sm:hidden" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0a4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="hidden sm:inline">{t('nav.login')}</span>
                </LocalizedLink>
                <LocalizedLink to="/signup" className="px-3 sm:px-4 py-2 text-sm font-semibold rounded-full bg-green-700 text-white hover:bg-green-800 transition whitespace-nowrap">{t('nav.signup')}</LocalizedLink>
              </>
            ) : (
              <button
                onClick={handleSignOut}
                className="px-3 sm:px-4 py-2 text-sm font-semibold rounded-full bg-red-600 text-white hover:bg-red-700 transition whitespace-nowrap"
              >
                {t('nav.logout')}
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer with Compliance */}
      <footer className="bg-gray-800 text-white py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 text-center sm:text-left">
          <div className="grid sm:grid-cols-3 gap-8 border-b border-gray-700 pb-8 mb-6">
            {/* Branding */}
            <div>
              <h4 className="text-xl font-black text-red-400 mb-2">
                Kris <span className="text-green-500">Kindler</span>
              </h4>
              <p className="text-gray-400 text-sm">{t('footer.branding')}</p>
            </div>

            {/* Legal/Info Links */}
            <div className="space-y-2">
              <h5 className="font-semibold text-gray-300">{t('footer.information')}</h5>
              <a href="#" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.howItWorks')}</a>
              <a href="#" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.faq')}</a>
              <a href="#" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.contactUs')}</a>
            </div>

            {/* Compliance */}
            <div className="space-y-2">
              <h5 className="font-semibold text-gray-300">{t('footer.legalPrivacy')}</h5>
              <LocalizedLink to="/privacy-policy" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.privacyPolicy')}</LocalizedLink>
              <LocalizedLink to="/terms-of-use" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.termsOfService')}</LocalizedLink>
              <LocalizedLink to="/accessibility" className="block text-sm text-gray-400 hover:text-red-400 transition">{t('footer.accessibility')}</LocalizedLink>
            </div>
          </div>

          {/* Affiliate Disclosure */}
          <p className="text-xs text-gray-500 mt-6 text-center">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
